<script setup lang="ts">
import type { DSImage3rdParty, DSStory } from '@/types'
import { toast } from 'vue-sonner'
import { usePendingPromises } from 'vuefire'

definePageMeta({
  layout: 'gradient',
})

const localePath = useLocalePath()
const route = useRoute()
const router = useRouter()
const { $unsplash } = useNuxtApp()
const { isUserLoggedIn } = storeToRefs(useUserStore())
const { public: { siteUrl } } = useRuntimeConfig()
const isCrawler = useState<boolean>('isCrawler')
const storyId = route.params?.storyId as string

const {
  getStoryById,
  getStoryCoverImage,
  deleteStoryById,
} = useStoryBase()
const story = getStoryById(storyId)
await usePendingPromises()

if (!story.value) {
  throw showError({
    statusCode: 404,
    fatal: true,
    message: 'story not found',
  })
}

const coverImage = await getStoryCoverImage(story.value)

const {
  topicTitle,
  shortContent,
  content,
  topicId,
  author,
  createdDate,
} = unref(story) as DSStory

const is3rdPartyImage = coverImage['3rdParty'] !== null

const isEditingStatus = ref(false)
const isEditDrawerShow = ref(false)
const isCommentVisible = ref(false)

const shouldShowRestrictedAccess = computed(() => {
  return !isUserLoggedIn.value && !isCrawler.value
})

function editStory() {
  isEditDrawerShow.value = false
  router.push(localePath(`/repost/${storyId}`))
}

async function deleteStory() {
  isEditDrawerShow.value = false
  isEditingStatus.value = true
}

async function onStoryDelete() {
  isEditingStatus.value = true
  await deleteStoryById(storyId)
  toast.info('故事已刪除')
  router.replace(localePath('/profile'))
}

const pageTitle = computed(() => `${story.value?.topicTitle} by ${story.value?.author}`)

// useHead({
//   title: pageTitle,
//   meta: [
//     { name: 'description', content: shortContent },
//     { property: 'og:title', content: pageTitle },
//     { property: 'og:description', content: shortContent },
//     { property: 'og:image', content: coverImage.url },
//     { name: 'twitter:card', content: 'summary_large_image' },
//   ],
// })

useSeoMeta({
  title: pageTitle,
  ogTitle: pageTitle,
  description: shortContent,
  ogDescription: shortContent,
  ogImage: coverImage.url,
  twitterCard: 'summary_large_image',
  ogUrl: `${siteUrl}/stories/${storyId}`,
})

onMounted(() => {
  if (is3rdPartyImage) {
    $unsplash.photos.trackDownload({
      downloadLocation: (story.value!.coverImage as DSImage3rdParty).apiUrl,
    })
  }
})
</script>

<template>
  <div class="flex flex-col justify-end md:h-[32rem] relative">
    <div
      class="relative pt-[100%] bg-cover bg-center bg-no-repeat"
      :style="{ backgroundImage: `url(${coverImage?.url})` }"
    />
    <div class="bg-silver mx-2 px-6 translate-y-[-25px]">
      <h1 class="pt-5 font-handwriting md:text-center text-2xl" v-html="topicTitle" />
      <div class="flex items-center justify-between mt-6 text-sm">
        <p class="text-neutral-500 font-handwriting">
          by {{ author }}
        </p>
        <p class="text-neutral-500 font-handwriting">
          {{ createdDate }}
        </p>
      </div>
    </div>
  </div>

  <div class="pt-1 px-6">
    <div class="relative">
      <article
        class="px-2 mb-8 prose prose-sm text-neutral-700 min-h-[42px]"
        v-html="content"
      />
      <!-- Login overlay -->
      <client-only>
        <div v-if="shouldShowRestrictedAccess" class="flex flex-col gap-y-4 absolute inset-0 flex justify-center items-center bg-gradient-to-b from-[rgba(235,235,235,0.31)] to-[#EBEBEB] backdrop-blur-sm">
          <p class="text-neutral-700 text-sm">
            請登入解鎖來看陌生人的故事
          </p>
          <ui-button
            v-check-login
            class="rounded-full"
          >
            登入
          </ui-button>
        </div>
      </client-only>
    </div>
    <aside v-if="is3rdPartyImage" class="my-8 text-neutral-500 text-xs px-2">
      Photo by <a
        class="underline"
        :href="`https://unsplash.com/@${coverImage?.username}?utm_source=Dear%20Stranger&utm_medium=referral`"
        target="_blank"
      >{{ coverImage?.owner }}</a> on <a
        class="underline"
        href="https://unsplash.com/?utm_source=Dear%20Stranger&utm_medium=referral" target="_blank"
      >{{ coverImage['3rdParty'] }}</a>
    </aside>

    <story-action-bar
      v-model="isCommentVisible"
      :story="story"
      :story-id="storyId"
      @toggle-edit="isEditDrawerShow = !isEditDrawerShow"
    />

    <client-only>
      <common-comment-block
        v-if="isCommentVisible"
        model-type="stories"
        :model-id="storyId"
      />
    </client-only>

    <story-wall :story-id="storyId" :topic-id="topicId" />
  </div>

  <common-compose-button v-show="!isCommentVisible" :redirect-url="`/posts/${topicId}`" display-text="種 故事" />

  <ui-drawer v-model:open="isEditDrawerShow">
    <ui-drawer-content class="bg-neutral-100 pb-safe">
      <ui-drawer-header hidden>
        <ui-drawer-title>編輯</ui-drawer-title>
        <ui-drawer-description>編輯文章或刪除文章</ui-drawer-description>
      </ui-drawer-header>

      <div class="flex flex-col items-center p-5 bg-neutral-100 opacity-[93]">
        <ui-button
          class="w-full px-6 py-3 h-auto font-handwriting justify-start rounded-bl-none rounded-br-none"
          variant="secondary"
          @click="editStory"
        >
          <div class="p-1 at-center rounded-md bg-neutral-100">
            <i i-ph-pencil-simple-fill style="font-size: 20px; color: #171717" />
          </div>

          <span class="ml-2 leading-normal text-left text-neutral-900">編輯</span>
        </ui-button>
        <ui-separator />
        <ui-button
          class="w-full px-6 py-3 h-auto font-handwriting justify-start rounded-tl-none rounded-tr-none"
          variant="secondary"
          @click="deleteStory"
        >
          <div class="p-1 at-center rounded-md bg-neutral-100">
            <i i-ph-trash-fill style="font-size: 20px; color: #171717" />
          </div>
          <span class="ml-2 leading-normal text-left text-red-600">刪除</span>
        </ui-button>
      </div>
    </ui-drawer-content>
  </ui-drawer>

  <ui-alert-dialog v-model:open="isEditingStatus">
    <ui-alert-dialog-content>
      <ui-alert-dialog-header>
        <ui-alert-dialog-title>刪除此篇故事？</ui-alert-dialog-title>
        <ui-alert-dialog-description>故事被刪除之後將無法回復，請確定你想要刪除故事嗎？</ui-alert-dialog-description>
      </ui-alert-dialog-header>
      <ui-alert-dialog-footer>
        <ui-alert-dialog-cancel>
          保留故事
        </ui-alert-dialog-cancel>
        <ui-alert-dialog-action @click="onStoryDelete">
          刪除
        </ui-alert-dialog-action>
      </ui-alert-dialog-footer>
    </ui-alert-dialog-content>
  </ui-alert-dialog>
</template>
